// Este componente es un layout que se utiliza para las paginas
// Que tienen un card en el centro de la pantalla en desktop
// Y en Mobile se muestra un div parecido al "bottom sheet"

import { PropsWithChildren, ReactElement } from 'react';
import { useMedia } from 'react-use';

import LogoBlue from '@/assets/ic_gdt-blue.svg';
import LogoWhite from '@/assets/ic_gdt-white.svg';
import { Card, Footer, FullHeight, FooterComplex } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { cn } from '@/utils';
import { COLORS } from '@/utils/constants';

type Props = PropsWithChildren<{
  className?: string;
  cardClassName?: string;
  innerCardClassName?: string;
  title?: string | ReactElement;
  bgClassName: string;
  bgLabel: string;
  showCardLogo?: boolean;
  footerComplex?: boolean;
  isPremium?: boolean;
  showFooter?: boolean;
}>;

const CardSheetLayout = ({
  className,
  title,
  children,
  bgClassName,
  bgLabel,
  cardClassName,
  innerCardClassName,
  showCardLogo = true,
  footerComplex = false,
  isPremium = false,
  showFooter = false,
}: Props) => {
  const { isDesktop } = useIsDesktop();

  // Para resolver un bug de espaciado en resoluciones de 1280px a 1360px
  // Estos hooks solo viven en este componente, y son propias de este componente
  const isSmallDesktop = useMedia(
    '(min-width: 1024px) and (max-width: 1359px)'
  );
  const isDefaultDesktop = useMedia(
    '(min-width: 1360px) and (max-width: 1399px)'
  );
  const isLargeDesktop = useMedia('(min-width: 1400px)');

  const footerRender = (
    <>
      {showFooter ? (
        footerComplex ? (
          <FooterComplex isPremium={isPremium} />
        ) : (
          <Footer className="hidden lg:block" />
        )
      ) : null}
    </>
  );

  return (
    <FullHeight className="flex flex-col">
      <main className="flex-1 flex flex-col relative !min-h-[550px]">
        <div
          aria-label={bgLabel}
          className={cn(
            'flex items-center justify-center relative flex-1 overflow-hidden lg:max-h-none py-5 bg-gdt-blue-dark',
            bgClassName
          )}
          style={{ backgroundColor: COLORS['gdt-blue'].dark }}
        >
          <figure className="relative z-[1] lg:hidden">
            <img
              alt="logo de grandt"
              className="h-auto w-[7rem]"
              src={LogoWhite}
            />
          </figure>
        </div>
        <section
          className={cn(
            'bg-gdt-blue-dark lg:bg-transparent lg:top-[50%] lg:translate-y-[-50%] lg:left-[50%] lg:translate-x-[-50%] lg:absolute',
            className
          )}
        >
          <Card
            className={cn(
              'lg:w-[27.5rem] w-auto px-6 pt-11 lg:max-h-none lg:h-auto rounded-bl-none rounded-br-none lg:rounded-b-lg',
              cardClassName,
              {
                'py-10': isSmallDesktop,
                'py-16': isDefaultDesktop,
              }
            )}
          >
            {showCardLogo && (
              <figure className="hidden lg:block lg:mb-14">
                <img
                  alt="logo de grandt"
                  className="m-auto w-[7rem] lg:w-[4.375rem]"
                  src={LogoBlue}
                />
              </figure>
            )}
            {title && (
              <h1
                className={cn(
                  isDesktop ? 'text-gdt-gray' : 'text-gdt-gray-2',
                  'uppercase font-normal font-bebas text-3xl text-center leading-8'
                )}
              >
                {title}
              </h1>
            )}
            <div
              className={cn(
                'lg:max-w-[21rem] w-full lg:m-auto',
                innerCardClassName
              )}
            >
              {children}
            </div>
          </Card>
        </section>
      </main>
      {isLargeDesktop && footerRender}
      {(isDefaultDesktop || isSmallDesktop) && footerRender}
    </FullHeight>
  );
};

export default CardSheetLayout;
